import { BASE_API_URL } from '@app/app-constants'
import { roundForDisplay } from '@app/components/Molecules/Charts/utils/utils'
import { LiveLayer } from '@app/modules/Live/Live.types'
import { LiveFeatureSection, LiveFeatureRow } from './LiveSelectedTabBody'

export const formatRawPropertyLabel = (rawProperty: string): string => {
  return rawProperty
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export async function buildLiveFeatureSection(
  properties: Record<string, string | number>,
  liveLayer: LiveLayer,
): Promise<LiveFeatureSection> {
  const liveFeatureRows: LiveFeatureRow[] = liveLayer.feature_properties
    ? liveLayer.feature_properties.map((featureProperty) => {
        let value = properties[featureProperty.property]
        if ((value === null || value === undefined) && featureProperty.if_null)
          value = featureProperty.if_null
        else {
          if (featureProperty.type === 'timestamp' && value)
            value = new Date(value).toLocaleString()
          if (featureProperty.unit)
            value = `${roundForDisplay(value as number, featureProperty.unit)} ${featureProperty.unit}`
        }

        if (featureProperty.label)
          return { label: featureProperty.label, value, type: featureProperty.type }

        const label = formatRawPropertyLabel(featureProperty.property)
        return { label, value, type: featureProperty.type }
      })
    : Object.entries(properties).map(([propertyKey, propertyValue]) => ({
        label: formatRawPropertyLabel(propertyKey),
        value: propertyValue as string | number,
      }))

  let featureLastUpdated: Date | null = null
  const lastUpdatedCfg = liveLayer.last_updated_config

  const rows = await Promise.all(
    liveFeatureRows
      .filter(({ value }) => value !== null && value !== undefined && value !== '')
      .map(async (liveFeatureRow) => {
        if (liveFeatureRow.type === 'image' && lastUpdatedCfg?.source === 'imageHeader') {
          try {
            const rawUrl = String(liveFeatureRow.value).replace(lastUpdatedCfg.proxy_url, '')
            const proxiedUrl = `${BASE_API_URL}/layer/live_feeds/${liveLayer.id}/proxy/${rawUrl}?last_updated_proxy=true`
            if (proxiedUrl && proxiedUrl !== rawUrl) {
              const imgResponse = await fetch(proxiedUrl, {
                method: 'HEAD',
                credentials: 'include',
              })
              const val = imgResponse.headers.get(lastUpdatedCfg.field)
              if (val) featureLastUpdated = new Date(val)
            }
          } catch {
            // silently fail
          }
        }
        return liveFeatureRow
      }),
  )

  if (liveLayer.lastUpdated && liveLayer.lastUpdated !== 'None') {
    featureLastUpdated = new Date(liveLayer.lastUpdated as string | Date)
  }

  const hoursSinceLastUpdate = featureLastUpdated
    ? (Date.now() - featureLastUpdated.getTime()) / (1000 * 60 * 60)
    : 0

  return {
    layerName: liveLayer.display_name ?? '',
    lastUpdated: featureLastUpdated,
    isStale: hoursSinceLastUpdate > 24,
    rows,
  }
}
